import { OmitStrict } from "@amx/ux-twoui-utils";
import React from "react";
import { Size } from "../../../../shared/constants";
import { validateProps } from "../../../../shared/props";
import { ComponentValidator, validateComponent } from "../../../../shared/validation";
import { useViewport } from "../../../../styles";
import { ActionButtonProps } from "../../../Buttons/ActionButton/ActionButton";
import { Media } from "../../../Media/Media";
import { MediaImage } from "../../../Media/Media.types";
import { SlotStack, SlotStackItem } from "../../../Stacks/SlotStack/SlotStack";
import { Stack, StackItem } from "../../../Stacks/Stack/Stack";
import { ListItemCanvas, ListItemCanvasExternalProps, ListItemCanvasProps } from "./ListItemCanvas";

type OmitActions<T extends BaseFeedListItemProps> = OmitStrict<T, "actions">;

interface BaseFeedListItemProps extends ListItemCanvasExternalProps {
    /**
     * Image to render at the beginning of the feed item
     */
    readonly image: MediaImage;

    /** Size of image; defaults to "md" */
    readonly imageSize?: Size;

    /**
     * JSX Elements to put into the "actions" slots.
     */
    readonly actions?: readonly JSX.Element[];
}

export interface FeedListItemProps extends OmitActions<BaseFeedListItemProps> {
    /**
     * Text to show beside image.
     */
    readonly text: string | JSX.Element;

    /**
     * Props for an ActionButton
     */
    readonly actionProps: ActionButtonProps;
}

interface BaseFeedListItemInternalProps extends BaseFeedListItemProps, ListItemCanvasProps { }

export const BaseFeedListItem: React.FC<BaseFeedListItemProps> = (props) => {
    return <BaseFeedListItemInternal  {...props} />;
};

/**
 * The maximum number of actions supported.
 */
const maxActionsSupported = 3;

const baseFeedListItemInternalValidator = (actionLength: number): ComponentValidator => (
    () => (actionLength > maxActionsSupported ? `BaseFeedListItem supports up to ${maxActionsSupported} action slots` : undefined)
);

export const BaseFeedListItemInternal: React.FC<BaseFeedListItemInternalProps> = ({
    image,
    imageSize = "md",
    children,
    groupSpacerNestingDepth,
    onClickProps,
    backgroundColor,
    actions,
    disabled = false,
    showDivider = false,
    ...otherProps
}) => {
    validateProps("BaseFeedListItemInternal", otherProps);

    const viewportType = useViewport();

    let renderedActions: JSX.Element = <></>;
    if (actions) {
        validateComponent("BaseFeedListItemInternal", [baseFeedListItemInternalValidator(actions.length)]);

        // in viewports "xl" and larger, the actions are expanded using "slots"
        if (["xl", "xxl", "xxxl"].includes(viewportType)) {
            renderedActions = (
                <Stack verticalAlign="center" type="slots" totalSlots={actions.length}>
                    {actions.map((action, i) => (
                        <StackItem key={action.key || i} slots={1}>
                            {!disabled ? action : React.cloneElement(action, { disabled })}
                        </StackItem>
                    ))}
                </Stack>
            );
        } else {
            renderedActions = (
                <Stack verticalAlign="center" horizontal={true} horizontalAlign={["s", "m"].includes(viewportType) ? "end" : "start"}>
                    {actions.map((action, i) => (
                        <StackItem key={action.key || i}>
                            {!disabled ? action : React.cloneElement(action, { disabled })}
                        </StackItem>
                    ))}
                </Stack>
            );
        }
    }

    let totalSlots: number;
    let actionStackItemsSlots: number;
    let mediaSlots: number;

    // tslint:disable: no-magic-numbers
    switch (viewportType) {
        case "s":
            totalSlots = 4;
            actionStackItemsSlots = actions?.length ? 1 : 0;
            mediaSlots = totalSlots - actionStackItemsSlots;
            break;

        case "m":
        case "l":
            totalSlots = 6;
            actionStackItemsSlots = actions?.length ? 2 : 0;
            mediaSlots = totalSlots - actionStackItemsSlots;
            break;

        case "xl":
            totalSlots = 12;
            actionStackItemsSlots = actions ? actions.length * 2 : 0;
            mediaSlots = totalSlots - actionStackItemsSlots;
            break;

        case "xxl":
        case "xxxl":
            totalSlots = 12;
            actionStackItemsSlots = actions ? actions.length * 2 : 0;
            mediaSlots = totalSlots - actionStackItemsSlots - 1; // minus 1 for the last unused colum
            break;
    }
    // tslint:enable: no-magic-numbers

    return (
        <ListItemCanvas groupSpacerNestingDepth={groupSpacerNestingDepth} onClickProps={onClickProps} backgroundColor={backgroundColor} showDivider={showDivider}>
            <SlotStack totalSlots={totalSlots} verticalAlign="center"  {...otherProps}>
                <SlotStackItem slots={mediaSlots}>
                    <Media disabled={disabled} size={imageSize} image={image} padding="2px 0 2px 16px">
                        {children}
                    </Media>
                </SlotStackItem>
                {actionStackItemsSlots && (
                    <SlotStackItem slots={actionStackItemsSlots}>
                        {renderedActions}
                    </SlotStackItem>
                )}
            </SlotStack>
        </ListItemCanvas>
    );
};
